/* Shared cart recommendation controls. Shopify owns cart rendering and prices. */
(() => {
  'use strict';
  if (customElements.get('park-cart-upsell')) return;
  let pending = false;
  class ParkCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.addEventListener('click', event => {
        const dialog = event.target.closest('[data-upsell-dialog]');
        if (dialog) {
          // This modal has its own backdrop and close control inside the cart modal.
          event.stopPropagation();
          if (event.target.closest('[data-upsell-close]') || event.target === dialog) {
            dialog.close();
            return;
          }
        }
        const choose = event.target.closest('[data-upsell-choose]');
        if (choose && (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
        if (choose && this.contains(choose)) {
          event.preventDefault();
          if (choose.disabled || pending) return;
          const picker = choose.closest('[data-upsell-product]').querySelector('[data-upsell-dialog]');
          picker.querySelectorAll('[data-upsell-variant]').forEach(input => { input.checked = false; });
          picker.querySelector('[data-upsell-add]').disabled = true;
          this.returnFocus = choose;
          picker.showModal();
          picker.querySelector('[data-upsell-variant]')?.focus();
          return;
        }
        const button = event.target.closest('[data-upsell-add]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        this.add(button);
      }, options);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-upsell-variant]')) return;
        const dialog = event.target.closest('[data-upsell-dialog]');
        if (dialog) dialog.querySelector('[data-upsell-add]').disabled = !dialog.querySelector('[data-upsell-variant]:checked');
      }, options);
      this.addEventListener('cancel', event => {
        if (event.target.matches('[data-upsell-dialog]')) event.stopPropagation();
      }, {...options, capture: true});
      this.addEventListener('close', event => {
        if (!event.target.matches('[data-upsell-dialog]')) return;
        if (this.returnFocus?.isConnected) this.returnFocus.focus();
        this.returnFocus = null;
      }, {...options, capture: true});
    }
    disconnectedCallback() {
      this.querySelectorAll('[data-upsell-dialog][open]').forEach(dialog => dialog.close());
      this.controller?.abort();
      this.controller = null;
      this.returnFocus = null;
    }
    async add(submitter) {
      if (pending || submitter.disabled) return;
      const card = submitter.closest('[data-upsell-product]');
      const picker = submitter.closest('[data-upsell-dialog]');
      const choice = card?.querySelector('[data-upsell-variant]:checked');
      if (picker && !choice) return;
      const variantId = choice?.value || submitter.dataset.upsellAdd;
      const quantity = Number(choice?.dataset.quantity || 1);
      if (!variantId || !Number.isInteger(quantity) || quantity < 1) return;
      const button = picker ? card.querySelector('[data-upsell-choose]') : submitter;
      const standalone = this.dataset.openCart === 'true';
      const drawer = this.closest('[data-park-cart-notification]');
      const flow = window.ParkCartNotificationFlow;
      const drawerFlow = drawer ? flow : null;
      if (drawerFlow?.beginAdd?.() === false) return;
      if (picker) picker.close();
      const lifetime = this.controller;
      const active = () => this.isConnected && this.controller === lifetime;
      pending = true;
      const status = this.querySelector('[data-upsell-status]');
      const label = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = submitter.dataset.loading || status.dataset.loading;
      status.textContent = status.dataset.loading;
      let refresh = true;
      let uncertain = false, result;
      const mutationController = new AbortController();
      const mutationTimeout = setTimeout(() => mutationController.abort(), 10000);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', signal: mutationController.signal,
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({items: [{id: variantId, quantity}], ...flow?.sectionRequest()})
        });
        result = await response.json();
        if (!response.ok || result.status >= 400) {
          const error = new Error(typeof result.description === 'string' ? result.description : status.dataset.error);
          error.cartAPI = true;
          throw error;
        }
        if (active()) {
          status.textContent = status.dataset.success;
          if (this.closest('[data-park-cart-notification]')) {
            card.classList.add('is-added');
            button.textContent = this.dataset.addedLabel;
            button.setAttribute('aria-label', this.dataset.addedLabel);
            button.disabled = true;
            delete button.dataset.drawerLocked;
            window.ParkCartNotification?.show(result, [{id: variantId, quantity}]);
          } else if (standalone) {
            window.ParkCartNotification?.show(result, [{id: variantId, quantity}]);
            status.textContent = '';
          }
        }
      } catch (error) {
        // A lost response may still have changed the cart. Never retry the mutation.
        refresh = !error.cartAPI;
        uncertain = refresh;
        if (active()) status.textContent = error.cartAPI ? error.message : status.dataset.error;
      } finally {
        clearTimeout(mutationTimeout);
        let rendered = false;
        try {
          rendered = await drawerFlow?.endAdd?.(refresh ? (uncertain ? undefined : result) : false);
        } catch (_) {
          refresh = true;
          uncertain = true;
          if (active()) status.textContent = status.dataset.error;
        } finally {
          pending = false;
        }
        if (button.isConnected) {
          button.disabled = standalone ? false : refresh;
          button.removeAttribute('aria-busy');
          if (standalone || !refresh || uncertain) button.textContent = label;
          if (refresh && !uncertain && drawer?.open && [document.body, button, submitter].includes(document.activeElement)) {
            drawer.querySelector('[data-cart-notification-close]')?.focus();
          }
        }
        if (refresh) document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {
          bubbles: true, detail: {openDrawer: false, refreshRecommendations: uncertain, cart: uncertain ? undefined : result, cartDrawerUpdated: Boolean(rendered)}
        }));
      }
    }
  }
  customElements.define('park-cart-upsell', ParkCartUpsell);
})();
